/**
 * product-zoom.js
 * Lupa de zoom para la galería de productos de Termox.
 *
 * Estrategia:
 *  - Detecta el contenedor `.media` del slide activo.
 *  - Construye un "snapshot virtual" que incluye la imagen del producto
 *    MÁS los overlays de personalización (#texto-personalizado, #imagen-personalizado).
 *  - Muestra una lupa circular (div fixed) que contiene ese snapshot escalado,
 *    centrado en la posición del cursor.
 *  - En móviles (≤768 px) la lupa no se activa.
 */

(function () {
  'use strict';

  /* ── Configuración ─────────────────────────────────────────── */
  const ZOOM_FACTOR = 1.8;   // factor de ampliación (default: 2.5)
  const LENS_SIZE   = 170;   // diámetro de la lupa en px
  const LENS_HALF   = LENS_SIZE / 2;
  const MOBILE_BP   = 1;   // por debajo de este ancho no se activa

  /* ── Estado global ─────────────────────────────────────────── */
  let lens  = null;
  let inner = null;   // div inside lens that holds the clone
  let clone = null;

  /* ═══════════════════════════════════════════════════
     Crear/obtener la lupa
  ═══════════════════════════════════════════════════ */
  function getLens() {
    if (lens) return lens;

    lens = document.createElement('div');
    lens.id = 'pz-lens';
    lens.setAttribute('aria-hidden', 'true');
    Object.assign(lens.style, {
      position:     'fixed',
      width:        LENS_SIZE + 'px',
      height:       LENS_SIZE + 'px',
      borderRadius: '50%',
      overflow:     'hidden',
      border:       '2.5px solid rgba(255,255,255,0.9)',
      boxShadow:    '0 0 0 1.5px rgba(0,0,0,0.22), 0 10px 36px rgba(0,0,0,0.32)',
      pointerEvents:'none',
      zIndex:       '99999',
      display:      'none',
      background:   '#fff',
      willChange:   'left, top',
      transition:   'opacity 0.12s ease',
      opacity:      '0',
    });

    // Crosshair sutil
    const ch = document.createElement('div');
    ch.id = 'pz-crosshair';
    Object.assign(ch.style, {
      position:     'absolute',
      inset:        '0',
      borderRadius: '50%',
      pointerEvents:'none',
      zIndex:       '3',
      background: [
        'linear-gradient(rgba(255,255,255,0) 48.5%, rgba(255,255,255,0.4) 49%, rgba(255,255,255,0.4) 51%, rgba(255,255,255,0) 51.5%)',
        'linear-gradient(90deg, rgba(255,255,255,0) 48.5%, rgba(255,255,255,0.4) 49%, rgba(255,255,255,0.4) 51%, rgba(255,255,255,0) 51.5%)',
      ].join(', '),
    });
    lens.appendChild(ch);

    // Inner scroll area (clips the clone)
    inner = document.createElement('div');
    inner.id = 'pz-inner';
    Object.assign(inner.style, {
      position: 'absolute',
      inset:    '0',
      overflow: 'hidden',
      zIndex:   '1',
    });
    lens.appendChild(inner);

    document.body.appendChild(lens);
    return lens;
  }

  /* ═══════════════════════════════════════════════════
     Obtener el contenedor a clonar.
     Buscamos el slide activo (.is-active .media).
     Los overlays de personalización (#texto-personalizado,
     #imagen-personalizado) están absolutamente posicionados
     en el slider-component, NO dentro de .media, por lo que
     necesitamos capturar su posición relativa al .media y
     clonarlos dentro de nuestro snapshot virtual.
  ═══════════════════════════════════════════════════ */
  function getActiveMedia() {
    return (
      document.querySelector('.product__media-item.is-active .media') ||
      document.querySelector('.product__media-list .product__media-item:first-child .media')
    );
  }

  /* ═══════════════════════════════════════════════════
     Construir/actualizar el clon virtual dentro de la lupa
  ═══════════════════════════════════════════════════ */
  function buildSnapshot(mediaEl) {
    // Limpiar clon anterior
    if (clone) { clone.remove(); clone = null; }

    const mediaRect = mediaEl.getBoundingClientRect();

    // Wrapper del snapshot (mismo tamaño que .media)
    const snap = document.createElement('div');
    snap.id = 'pz-clone';
    Object.assign(snap.style, {
      position:      'absolute',
      top:           '0',
      left:          '0',
      width:         mediaRect.width  + 'px',
      height:        mediaRect.height + 'px',
      pointerEvents: 'none',
      userSelect:    'none',
      overflow:      'visible',
      transformOrigin: '0 0',
    });

    // Clonar el .media completo (imagen del producto)
    const mediaClone = mediaEl.cloneNode(true);
    Object.assign(mediaClone.style, {
      position: 'absolute',
      top:      '0',
      left:     '0',
      width:    '100%',
      height:   '100%',
      margin:   '0',
      padding:  '0',
    });
    snap.appendChild(mediaClone);

    // Clonar overlays de personalización
    const textoEl   = document.getElementById('texto-personalizado');
    const textoEl2  = document.getElementById('texto-personalizado-2');
    const imagenEl  = document.getElementById('imagen-personalizado');

    [textoEl, textoEl2, imagenEl].forEach(function (el) {
      if (!el) return;

      // ── Guardas de contenido ──────────────────────────────────────
      // 1. <p> de texto: omitir si no tiene texto escrito por el usuario.
      //    Sin esto, un <p> vacío con CSS "position:absolute; top:49.8%"
      //    tiene dimensiones reales pero su centro cae FUERA del .media
      //    (el slider-component es más alto), generando la posición incorrecta.
      if ((el.tagName === 'P') && el.textContent.trim() === '') return;

      // 2. <img> o <svg> de imagen: omitir si no tiene src válido.
      if (el.tagName === 'IMG') {
        const src = (el.getAttribute('src') || '').trim();
        if (!src || src === ' ' || src === '') return;
      }
      if (el.tagName === 'SVG') {
        // SVG inyectado por colorizarSVG: solo omitir si no tiene contenido gráfico
        if (!el.querySelector('path, circle, rect, use, g')) return;
      }

      const elRect = el.getBoundingClientRect();
      // Sin dimensiones → ignorar
      if (elRect.width === 0 && elRect.height === 0) return;

      // ─────────────────────────────────────────────────────────────
      // SOLUCIÓN DEFINITIVA: 
      // En lugar de calcular el centro y mantener el transform (que 
      // puede causar desplazamientos si el ancho del clon difiere 
      // por subpíxeles o fuentes no cargadas), copiamos directamente 
      // la esquina superior izquierda visual exacta y ANULAMOS el 
      // transform. Esto garantiza una copia pixel-perfect idéntica.
      // ─────────────────────────────────────────────────────────────

      // Esquina superior izquierda visual del elemento original
      const elLeft = elRect.left;
      const elTop  = elRect.top;

      // Posición relativa al borde superior-izquierdo de .media
      let relLeft = elLeft - mediaRect.left;
      let relTop  = elTop  - mediaRect.top;

      // ─────────────────────────────────────────────────────────────
      // AJUSTE MANUAL DE POSICIÓN SOLO EN EL ZOOM (EJE X / Y)
      // Si un producto específico necesita verse más a la derecha o izquierda
      // dentro de la lupa, puedes sumarle o restarle píxeles aquí.
      // ─────────────────────────────────────────────────────────────
      if (window.location.pathname.includes('light-goma-500-ml')) {
        // Valores positivos (ej: 5) mueven a la DERECHA.
        // Valores negativos (ej: -5) mueven a la IZQUIERDA.
        relLeft += 10; // <--- AJUSTA ESTE NÚMERO PARA EL EJE X
        
        // Si necesitas ajustar arriba/abajo, descomenta la siguiente línea:
        // relTop += 0; 
      }

      // ── Guarda de rango ───────────────────────────────────────────
      // Si el overlay está muy fuera del .media, ignorarlo.
      const MARGIN_X = elRect.width  + 10;
      const MARGIN_Y = elRect.height + 10;
      if (
        relLeft < -MARGIN_X || relLeft > mediaRect.width  + 10 ||
        relTop  < -MARGIN_Y || relTop  > mediaRect.height + 10
      ) return;

      const elClone = el.cloneNode(true);

      // Aplicar posición exacta y anular transform
      elClone.style.setProperty('position',       'absolute', 'important');
      elClone.style.setProperty('left',            relLeft + 'px', 'important');
      elClone.style.setProperty('top',             relTop  + 'px', 'important');
      elClone.style.setProperty('transform',       'none', 'important');
      elClone.style.setProperty('margin',          '0', 'important');
      elClone.style.setProperty('z-index',         '10', 'important');
      elClone.style.setProperty('pointer-events',  'none', 'important');

      snap.appendChild(elClone);
    });

    inner.appendChild(snap);
    clone = snap;
    return snap;
  }

  /* ═══════════════════════════════════════════════════
     Mover la lupa con el cursor
  ═══════════════════════════════════════════════════ */
  function moveLens(mediaEl, e) {
    if (!clone) return;

    const rect = mediaEl.getBoundingClientRect();
    const cx   = e.clientX;
    const cy   = e.clientY;

    // Posicionar la lupa centrada sobre el cursor
    lens.style.left = (cx - LENS_HALF) + 'px';
    lens.style.top  = (cy - LENS_HALF) + 'px';

    // Posición del cursor relativa al .media
    const rx = cx - rect.left;
    const ry = cy - rect.top;

    // Con zoom Z, el punto (rx*Z, ry*Z) del clon debe quedar en (LENS_HALF, LENS_HALF).
    // translate = (LENS_HALF - rx*Z) / Z  → luego scale() lo multiplica de vuelta.
    const tx = (LENS_HALF - rx * ZOOM_FACTOR) / ZOOM_FACTOR;
    const ty = (LENS_HALF - ry * ZOOM_FACTOR) / ZOOM_FACTOR;

    clone.style.transform = `scale(${ZOOM_FACTOR}) translate(${tx}px, ${ty}px)`;
  }

  /* ═══════════════════════════════════════════════════
     Adjuntar eventos a un contenedor .media
  ═══════════════════════════════════════════════════ */
  function attachToMedia(mediaEl) {
    // Evitar doble-registro
    if (mediaEl.dataset.pzAttached) return;
    mediaEl.dataset.pzAttached = '1';

    mediaEl.style.cursor = 'none';

    mediaEl.addEventListener('mouseenter', function () {
      if (window.innerWidth <= MOBILE_BP) return;
      const l = getLens();
      buildSnapshot(mediaEl);
      l.style.display  = 'block';
      // Forzar reflow para que la transición de opacidad funcione
      void l.offsetWidth;
      l.style.opacity  = '1';
    }, { passive: true });

    mediaEl.addEventListener('mouseleave', function () {
      if (!lens) return;
      lens.style.opacity = '0';
      // Ocultar después de la transición
      setTimeout(function () {
        if (lens) lens.style.display = 'none';
        if (clone) { clone.remove(); clone = null; }
      }, 130);
    }, { passive: true });

    mediaEl.addEventListener('mousemove', function (e) {
      if (window.innerWidth <= MOBILE_BP) return;
      if (!lens || lens.style.display === 'none') return;
      moveLens(mediaEl, e);
    }, { passive: true });
  }

  /* ═══════════════════════════════════════════════════
     Observer: reconstruir snapshot cuando los overlays
     cambian mientras la lupa está activa (el usuario
     escribe su personalización en tiempo real)
  ═══════════════════════════════════════════════════ */
  function watchOverlays() {
    const overlayIds = ['texto-personalizado', 'texto-personalizado-2', 'imagen-personalizado'];
    let rebuildTimer = null;

    function scheduleRebuild() {
      // La lupa debe estar visible
      if (!lens || lens.style.display === 'none' || !clone) return;
      // Debounce: reconstruir 80ms después del último cambio
      clearTimeout(rebuildTimer);
      rebuildTimer = setTimeout(function () {
        // Buscar el .media activo en este momento
        const activeMedia = (
          document.querySelector('.product__media-item.is-active .media') ||
          document.querySelector('.product__media-list .product__media-item:first-child .media')
        );
        if (activeMedia) buildSnapshot(activeMedia);
      }, 80);
    }

    overlayIds.forEach(function (id) {
      // El elemento puede reemplazarse por un SVG (colorizarSVG),
      // así que observamos el documento para capturar el nuevo elemento.
      const observer = new MutationObserver(scheduleRebuild);

      function observeEl() {
        const el = document.getElementById(id);
        if (el) {
          observer.observe(el, {
            childList:     true,
            subtree:       true,
            characterData: true,
            attributes:    true,
          });
        }
      }

      observeEl();

      // Si el elemento aún no existe o fue reemplazado, re-observar
      // cuando el DOM cambie a nivel de slider-component
      const sliderEl = document.getElementById('GalleryViewer-' +
        (document.querySelector('media-gallery[id]') || {}).id?.replace('MediaGallery-', '') || '');

      const parentObs = new MutationObserver(function () {
        observeEl();
        scheduleRebuild();
      });

      const sliderParent = document.querySelector('slider-component');
      if (sliderParent) {
        parentObs.observe(sliderParent, { childList: true, subtree: false });
      }
    });
  }

  /* ═══════════════════════════════════════════════════
     Inicializar en todos los slides de imagen
  ═══════════════════════════════════════════════════ */
  function init() {
    const mediaItems = document.querySelectorAll(
      '.product__media-list .product__media-item .media'
    );
    if (!mediaItems.length) return;

    mediaItems.forEach(function (mediaEl) {
      // Solo slides con imagen (no vídeos / modelos 3D)
      const img = mediaEl.querySelector('img');
      if (!img) return;
      attachToMedia(mediaEl);
    });
  }

  /* ═══════════════════════════════════════════════════
     Bootstrap
  ═══════════════════════════════════════════════════ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      watchOverlays();
    });
  } else {
    // DOM ya listo (script con defer carga tarde)
    init();
    watchOverlays();
  }

  // Re-inicializar si Shopify cambia variante y regenera slides
  document.addEventListener('variant:change', function () {
    // Resetear pzAttached para que los nuevos slides se registren
    document.querySelectorAll('[data-pz-attached]').forEach(function (el) {
      delete el.dataset.pzAttached;
    });
    init();
  });

})();
